import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { TASK_TYPES } from '../../api/endpoints';
import { toast } from 'react-toastify';
import { buildTaskTypeRelationParams } from './taskTypeApiHelpers';

const row = (label, value) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Grid>
);

const ViewTaskType = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(TASK_TYPES.BY_ID(id), {
          params: buildTaskTypeRelationParams(),
        });
        const payload = data?.data ?? data;
        if (!cancelled) setItem(payload);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load task type');
        navigate('/task-types');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading || !item) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  const hex = item.color && String(item.color).trim() ? String(item.color).trim() : '';
  const displayHex = hex.startsWith('#') ? hex : hex ? `#${hex}` : '';

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/task-types')} sx={{ mb: 2 }}>
          Back to task types
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {item.name ?? 'Task type'}
          </Typography>
          <Button variant="outlined" size="small" onClick={() => navigate(`/task-types/edit/${id}`)}>
            Edit
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {row('Status', item.status?.name ?? item.status?.label)}
              {row('Name', item.name)}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                  {displayHex ? (
                    <>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 0.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: displayHex,
                        }}
                      />
                      <Typography variant="body1">{displayHex}</Typography>
                    </>
                  ) : (
                    <Typography variant="body1">—</Typography>
                  )}
                </Box>
              </Grid>
              {row('Parent', item.parent?.name)}
              {row('Author', item.author?.name ?? item.author?.username)}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Description
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                  {item.description ?? '—'}
                </Typography>
              </Grid>
              {row('Created at', item.created_at)}
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewTaskType;
